// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일은 컨덕터가 재핸드오프 때 통째로 덮어쓴다 — 배선은 여기에만 둔다. 계약(Wiring)은 화면 파일이 낸다(문서 50).
//  최초 골격: pbo-conductor — 게시판 (rp14)
import dayjs from 'dayjs'
import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { canSelectShipper, selectableShippers, shipperScopeOf } from '@/components/system/shipperScope'
import { boardQueryKeys, useGetBoardPosts } from '@/services/board/useBoardQueries'
import type { BoardPostSummary, GetBoardPostsReq } from '@/services/board/types'
import { useGetShipperOptions } from '@/services/inventory/stockLedger/useStockLedgerQueries'
import { DATE_FORMAT } from '@/utils/consts'
import { queryClient } from '@/utils/queryClient'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { BOARD_BASE_PATH, BOARD_CATEGORIES, BOARD_PAGE_SIZE } from '../consts'
import type { Option, Row, RowsQuery, Wiring } from './page.generated'

/**
 * 진입 기본 조회 기간 3개월 — 조회영역에도 보이게 filterDefaults 로 넣는다.
 * ponytail: 생성본 presetRange 가 defaultPeriod 'month3M' 을 몰라(page.generated.tsx presetRange) 기본값이 비어 그려진다 — 생성기가 고치면 이 값과 같아진다.
 */
const defaultFilters = (): Record<string, unknown> => ({
  insertedAt: [dayjs().subtract(3, 'month').format(DATE_FORMAT.date), dayjs().format(DATE_FORMAT.date)],
})

/** [조회] 를 누른 조건 — 상세에 다녀와도 남고, 조회영역에도 복원한다(filterInitial). null 이면 진입 기본 조건. */
const searchedAtom = atom<Record<string, unknown> | null>(null)
/** 마지막 페이지 — 상세에 다녀와도 같은 페이지로 돌아온다(stateInitial·onState). */
let lastPager = { page: 1, size: BOARD_PAGE_SIZE }

const toYmd = (v: unknown): string | undefined => (typeof v === 'string' && v ? dayjs(v).format(DATE_FORMAT.dateSimple) : undefined)

export const toBoardPostsReq = (filters: Record<string, unknown>, pager?: { page: number; size: number }): GetBoardPostsReq => {
  const shipperCodes = Array.isArray(filters.shipperCodes) ? filters.shipperCodes.map(String).filter(Boolean) : []
  const [from, to]: unknown[] = Array.isArray(filters.insertedAt) ? filters.insertedAt : []
  const title = typeof filters.title === 'string' ? filters.title.trim() : ''
  const category = typeof filters.category === 'string' ? filters.category : ''

  return {
    ...(shipperCodes.length ? { shipperCodes } : {}),
    ...(category ? { category } : {}),
    ...(title ? { title } : {}),
    ...(toYmd(from) ? { fromDate: toYmd(from) } : {}),
    ...(toYmd(to) ? { toDate: toYmd(to) } : {}),
    page: Math.max((pager?.page ?? 1) - 1, 0),
    size: pager?.size ?? BOARD_PAGE_SIZE,
  }
}

export const toBoardRow = (item: BoardPostSummary): Row => ({
  key: item.id,
  id: item.id,
  notice: item.notice ? '공지' : '',
  category: item.category ?? '',
  title: item.title ?? '',
  insertedAt: item.insertedAt ? dayjs(item.insertedAt).format(DATE_FORMAT.date) : '',
  shipperCode: item.shipperCode,
  writerName: item.writer.name ?? '',
  viewCount: item.viewCount,
  commentCount: item.commentCount,
})

// 행 버튼은 렌더 밖(클릭)에서 불린다 — 표 슬롯(렌더 첫머리)이 받은 navigate 를 모듈에 둔다.
let navigate: ReturnType<typeof useNavigate> | null = null

const useBoardRows = (_demo: Row[], q: RowsQuery) => {
  navigate = useNavigate()
  const searched = useAtomValue(searchedAtom)
  const req = useMemo(() => toBoardPostsReq(searched ?? defaultFilters(), q.pager), [searched, q.pager])
  const { data, isFetching } = useGetBoardPosts(req)

  return { rows: (data?.items ?? []).map(toBoardRow), total: data?.pageInfo.totalCount ?? 0, loading: isFetching }
}

const useBoardShipperOptions = (): Option[] => {
  const scope = useMemo(() => shipperScopeOf(AuthStorage.user.get()), [])
  const { options } = useGetShipperOptions()
  return useMemo(
    () => (scope.locked ? selectableShippers(scope).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode })) : options),
    [options, scope],
  )
}

const CATEGORY_OPTIONS: Option[] = BOARD_CATEGORIES.map((c) => ({ value: c, label: c }))

export const wiring: Wiring = {
  rows: {
    ROWS: useBoardRows,
  },
  actions: {
    search: (filters) => {
      getDefaultStore().set(searchedAtom, { ...filters })
      // queryKey 는 값 비교라 같은 조건이면 캐시를 쓴다 — [조회] 는 다시 받는다(새 글 반영).
      return queryClient.invalidateQueries({ queryKey: boardQueryKeys.lists })
    },
    ROWS_rowAction: (_action, row) => {
      navigate?.(`${BOARD_BASE_PATH}/${String(row.id)}`)
    },
  },
  options: {
    BOARDSHIPPER_OPTIONS: useBoardShipperOptions,
    BOARDCATEGORY_OPTIONS: () => CATEGORY_OPTIONS,
  },
  filterDefaults: { boardFilterCard: (demo) => ({ ...demo, ...defaultFilters() }) },
  filterInitial: { boardFilterCard: (defaults) => getDefaultStore().get(searchedAtom) ?? defaults },
  // 생성본은 진입 때 이 값을 읽는다 — getter 로 마지막 페이지를 준다. 크기는 BE 상한(100) 안이어야 한다(생성본 기본 10000).
  get stateInitial() {
    return { pager: { pager: lastPager } }
  },
  onState: (s) => {
    lastPager = s.pager.pager
  },
  // 고를 화주가 하나뿐인 잠긴 계정은 화주 칸을 잠근다 — 서버가 허용 화주로 다시 거른다(ShipperScope). 화주 칸은 칸별 값만 읽는다.
  get filterDisabled() {
    return { shipperCodes: !canSelectShipper(shipperScopeOf(AuthStorage.user.get())) }
  },
  tableText: {
    tableGroup: { emptyText: '조회된 게시글이 없습니다.' },
  },
}
